import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCode,
  FaServer,
  FaSync,
  FaMobile,
  FaBug,
  FaCloud,
  FaLightbulb,
  FaCheckCircle,
  FaUsers,
  FaClock,
  FaShieldAlt,
  FaChartLine,
  FaRocket,
  FaCalculator
} from 'react-icons/fa';

const Services = () => {
  const services = [
    {
      title: 'Frontend Developers',
      description: 'Build modern, responsive user interfaces with exceptional performance and accessibility standards.',
      tech: 'React, Next.js, Vue, Angular, TypeScript',
      icon: <FaCode />,
      responsibilities: [
        'UI/UX implementation and optimization',
        'Component architecture and reusability',
        'Performance optimization and code splitting',
        'Responsive design and cross-browser compatibility',
        'State management and API integration'
      ],
      expertise: 'UI/UX, Performance, Responsive Design, Modern Frameworks'
    },
    {
      title: 'Backend Developers',
      description: 'Design and maintain scalable backend systems, APIs, and database architectures for enterprise applications.',
      tech: 'Node.js, Python, Java, .NET Core, Go',
      icon: <FaServer />,
      responsibilities: [
        'RESTful and GraphQL API development',
        'Database design and optimization',
        'Microservices architecture',
        'Authentication and authorization',
        'System scalability and performance tuning'
      ],
      expertise: 'APIs, Microservices, Database Design, System Architecture'
    },
    {
      title: 'Full Stack Developers',
      description: 'End-to-end development expertise reducing handoffs and accelerating delivery across the entire stack.',
      tech: 'MERN, MEAN, Django, Spring Boot, Full Stack JS/Python',
      icon: <FaSync />,
      responsibilities: [
        'Feature development from frontend to backend',
        'Full application lifecycle management',
        'Database design and API development',
        'UI implementation and user experience',
        'Deployment and DevOps collaboration'
      ],
      expertise: 'Product Development, Architecture, Full Stack Solutions'
    },
    {
      title: 'Mobile App Developers',
      description: 'Create production-ready mobile applications for iOS, Android, and cross-platform with native performance.',
      tech: 'Flutter, React Native, iOS (Swift), Android (Kotlin)',
      icon: <FaMobile />,
      responsibilities: [
        'Native and cross-platform app development',
        'App Store and Play Store deployment',
        'Performance optimization and memory management',
        'Push notifications and offline capabilities',
        'Mobile UI/UX best practices'
      ],
      expertise: 'Native Performance, App Store Deployment, Cross-Platform Development'
    },
    {
      title: 'QA & Automation Engineers',
      description: 'Ensure product quality through comprehensive testing strategies, manual testing, and automated test pipelines.',
      tech: 'Selenium, Cypress, Jest, Postman, TestNG, JUnit',
      icon: <FaBug />,
      responsibilities: [
        'Test strategy and test case development',
        'Automated test framework setup',
        'Regression testing and bug tracking',
        'Performance and load testing',
        'CI/CD pipeline integration'
      ],
      expertise: 'Test Automation, Quality Assurance, Testing Frameworks'
    },
    {
      title: 'DevOps & Cloud Engineers',
      description: 'Manage infrastructure, deployments, CI/CD pipelines, scaling, and system reliability in cloud environments.',
      tech: 'AWS, Azure, Docker, Kubernetes, Jenkins, Terraform',
      icon: <FaCloud />,
      responsibilities: [
        'Cloud infrastructure setup and management',
        'CI/CD pipeline configuration',
        'Container orchestration and deployment',
        'Monitoring, logging, and alerting',
        'Cost optimization and security compliance'
      ],
      expertise: 'CI/CD, Infrastructure, Cloud Management, Monitoring'
    }
  ];

  const benefits = [
    {
      icon: <FaUsers />,
      title: 'Pre-Vetted Talent',
      description: 'All engineers undergo rigorous technical interviews and background verification before joining your team.'
    },
    {
      icon: <FaClock />,
      title: 'Fast Onboarding',
      description: 'Get developers ready to work within 24-72 hours of requirement submission, not weeks.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Security & Compliance',
      description: 'Enterprise-grade security with NDAs, IP protection, and secure access controls for all resources.'
    },
    {
      icon: <FaChartLine />,
      title: 'Scalable Teams',
      description: 'Scale your team up or down based on project needs without long-term commitments or hiring delays.'
    }
  ];

  const engagementModels = [
    {
      title: 'Full-Time Dedicated',
      description: 'Ideal for long-term roadmap execution and core product development. Engineers work 40+ hours per week.',
      duration: '3+ months recommended'
    },
    {
      title: 'Part-Time Support',
      description: 'Best for feature development, maintenance, or specialized expertise. Flexible hours based on your needs.',
      duration: 'Flexible duration'
    },
    {
      title: 'Dedicated Team Pods',
      description: 'Pre-configured teams including developers, QA, and project oversight for structured delivery.',
      duration: '6+ months recommended'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '120px', backgroundColor: 'var(--bg-grey-light)', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
          zIndex: 1
        }}></div>
        <div className="container text-center" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="h1" style={{ marginBottom: '24px' }}>
            Dedicated Engineering Talent
          </h1>
          <p className="text-lg mb-6" style={{ maxWidth: '700px', margin: '0 auto 32px', fontSize: '20px', color: 'var(--text-medium)' }}>
            Choose the skills you need. Our engineers work as an extension of your internal team, 
            integrating seamlessly with your tools, processes, and culture.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/calculator" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Get Team Cost
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaRocket /> Schedule a Call
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Complete Engineering Team Solutions</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '700px', margin: '0 auto' }}>
              From frontend to cloud infrastructure, we provide every engineering role you need to build and scale your product.
            </p>
          </div>
          <div className="grid grid-2">
            {services.map((service, index) => (
              <div key={index} className="card" style={{ padding: '32px' }}>
                <div style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '20px' }}>
                  {service.icon}
                </div>
                <h3 className="h4 text-green mb-3">{service.title}</h3>
                <p className="mb-4" style={{ lineHeight: '1.6' }}>{service.description}</p>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--text-medium)', fontWeight: '600', marginBottom: '8px' }}>
                    Technologies:
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                    {service.tech}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--text-medium)', fontWeight: '600', marginBottom: '8px' }}>
                    Expertise:
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                    {service.expertise}
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'var(--bg-grey-light)',
                  padding: '16px',
                  borderRadius: 'var(--border-radius)',
                  marginTop: '16px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--primary-green)', marginBottom: '12px' }}>
                    Key Responsibilities:
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {service.responsibilities.map((resp, idx) => (
                      <li key={idx} style={{
                        fontSize: '13px',
                        color: 'var(--text-medium)',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}>
                        <FaCheckCircle style={{ color: 'var(--primary-green)', fontSize: '14px', marginTop: '2px', flexShrink: 0 }} />
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-grey">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Why Choose Our Engineering Services</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              We provide more than just developers - we deliver engineering partners who integrate seamlessly with your team.
            </p>
          </div>
          <div className="grid grid-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="card" style={{ padding: '32px', display: 'flex', gap: '24px' }}>
                <div style={{ fontSize: '40px', color: 'var(--primary-green)', flexShrink: 0 }}>
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="h4 mb-3" style={{ color: 'var(--text-dark)' }}>{benefit.title}</h3>
                  <p style={{ color: 'var(--text-medium)', lineHeight: '1.6' }}>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="h2 mb-4">Flexible Engagement Models</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-medium)', maxWidth: '600px', margin: '0 auto' }}>
              Choose the engagement model that best fits your project timeline and requirements.
            </p>
          </div>
          <div className="grid grid-3">
            {engagementModels.map((model, index) => (
              <div key={index} className="card" style={{ padding: '32px', textAlign: 'center' }}>
                <h3 className="h4 text-green mb-3">{model.title}</h3>
                <p style={{ color: 'var(--text-medium)', marginBottom: '16px', lineHeight: '1.6' }}>
                  {model.description}
                </p>
                <div style={{
                  backgroundColor: 'var(--bg-grey-light)',
                  padding: '12px',
                  borderRadius: 'var(--border-radius)',
                  fontSize: '14px',
                  color: 'var(--primary-green)',
                  fontWeight: '600'
                }}>
                  {model.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-grey">
        <div className="container text-center">
          <FaLightbulb style={{ fontSize: '48px', color: 'var(--primary-green)', marginBottom: '16px' }} />
          <h2 className="h2 mb-6">Not sure which roles fit your needs?</h2>
          <p className="text-lg mb-8" style={{ maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            Get personalized recommendations based on your project requirements, timeline, and technical stack. 
            Our team will help you build the perfect engineering team.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaLightbulb /> Get Team Recommendation
            </Link>
            <Link to="/calculator" className="btn btn-secondary btn-lg" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalculator /> Calculate Team Cost
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
